import { Anchor, Badge, Card, Group, Stack, Text } from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import { useSwipeGesture, SWIPE_THRESHOLD } from '../hooks/useSwipeGesture.js';
import { buildMapsUrl, objectPhase, profileLabel, type ObjectView } from '../lib/api.js';
import { FavoriteHeart } from './FavoriteHeart.js';
import { ScoreBadge } from './ScoreBadge.js';

interface Props {
  obj: ObjectView;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

function fmtPrice(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtArea(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${Number.isInteger(n) ? n : n.toFixed(1)} m²`;
}

function fmtMin(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${Math.round(n)} min`;
}

/** "gefunden 27.05. (vor 3 T)" — absolutes Datum + relativer Hinweis. */
export function fmtFound(ts: number | null | undefined): string {
  if (ts === null || ts === undefined) return '';
  const d = new Date(ts);
  const datum = d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const days = Math.floor((Date.now() - ts) / (24 * 60 * 60 * 1000));
  const rel = days <= 0 ? 'heute' : days === 1 ? 'gestern' : `vor ${days} T`;
  return `gefunden ${datum} · ${rel}`;
}

export function ListingCard({ obj, onSwipeLeft, onSwipeRight }: Props): JSX.Element {
  const navigate = useNavigate();
  const killed = (obj.score?.killedBy ?? null) !== null;
  const profileBadgeColor = obj.assignedProfile === 'B_buero' ? 'cyan' : 'violet';
  const phase = objectPhase(obj);

  const swipeable = Boolean(onSwipeLeft && onSwipeRight);
  const swipe = useSwipeGesture({
    onSwipeLeft: onSwipeLeft ?? (() => undefined),
    onSwipeRight: onSwipeRight ?? (() => undefined),
    onTap: () => navigate(`/objects/${obj.id}`),
    enableKeyboard: false, // multiple cards on page; keyboard only on dedicated Swipe page
  });

  const { dragX, isDragging, handlers, rootRef, style } = swipe;
  const dragRatio = swipeable ? Math.min(1, Math.abs(dragX) / SWIPE_THRESHOLD) : 0;
  const isPullLeft = swipeable && dragX < 0;
  const isPullRight = swipeable && dragX > 0;

  return (
    <div
      ref={swipeable ? rootRef : undefined}
      {...(swipeable ? handlers : {})}
      style={swipeable ? style : { cursor: 'pointer' }}
      onClick={swipeable ? undefined : () => navigate(`/objects/${obj.id}`)}
    >
      <Card
        padding="lg"
        style={{
          position: 'relative',
          overflow: 'hidden',
          opacity: killed ? 0.55 : 1,
          transition: 'transform 120ms ease, box-shadow 120ms ease',
        }}
      >
        {isPullRight && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(135deg, rgba(16,185,129,${dragRatio * 0.18}) 0%, transparent 80%)`,
              pointerEvents: 'none',
            }}
          />
        )}
        {isPullLeft && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(225deg, rgba(239,68,68,${dragRatio * 0.18}) 0%, transparent 80%)`,
              pointerEvents: 'none',
            }}
          />
        )}
        {dragRatio > 0.4 && isPullRight && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              border: '2px solid #10b981',
              borderRadius: 6,
              padding: '3px 8px',
              color: '#10b981',
              fontWeight: 800,
              fontSize: '0.85rem',
              letterSpacing: '0.08em',
              opacity: dragRatio,
              pointerEvents: 'none',
            }}
          >
            ★ HOT
          </div>
        )}
        {dragRatio > 0.4 && isPullLeft && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              border: '2px solid #ef4444',
              borderRadius: 6,
              padding: '3px 8px',
              color: '#ef4444',
              fontWeight: 800,
              fontSize: '0.85rem',
              letterSpacing: '0.08em',
              opacity: dragRatio,
              pointerEvents: 'none',
            }}
          >
            ✗ PASS
          </div>
        )}

        <Group align="flex-start" wrap="nowrap" gap="md">
          <Stack gap={2} align="center">
            <ScoreBadge score={obj.score?.effectiveTotal} size="md" killed={killed} />
            {obj.score &&
              obj.score.decayDays >= 0.5 &&
              obj.score.total - obj.score.effectiveTotal >= 0.01 && (
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.65rem' }}
                  title={`Raw ${obj.score.total.toFixed(2)} · ${obj.score.decayDays.toFixed(0)}d alt · 0.05/Tag Decay`}
                >
                  −{obj.score.decayDays.toFixed(0)}d
                </Text>
              )}
          </Stack>

          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            <Group gap={6} mb={2}>
              <Badge size="sm" color={profileBadgeColor} variant="light" radius="sm" tt="none">
                {profileLabel(obj.assignedProfile)}
              </Badge>
              <Badge size="sm" color={phase.color} variant="light" radius="sm" tt="none">
                {phase.label}
              </Badge>
            </Group>

            <Text fw={600} size="md" lineClamp={1} style={{ letterSpacing: '-0.01em' }}>
              {obj.listing?.title ?? '—'}
            </Text>
            <Text size="sm" c="dimmed" lineClamp={1}>
              {obj.address ?? '—'}
            </Text>
            {obj.listing?.firstSeenAt && (
              <Text size="xs" c="dimmed" style={{ fontSize: '0.7rem' }}>
                📅 {fmtFound(obj.listing.firstSeenAt)}
              </Text>
            )}

            {killed && obj.score?.killedBy && (
              <Text size="xs" c="red.7" mt={2}>
                ✕ {obj.score.killedBy}
              </Text>
            )}

            <Group gap="md" mt={6} wrap="wrap">
              <FactInline label="Fläche" value={fmtArea(obj.latestSnapshot?.areaSqm)} />
              <FactInline label="Miete" value={fmtPrice(obj.latestSnapshot?.price)} />
              <FactInline label="Pendel" value={fmtMin(obj.bestCommuteMin)} />
            </Group>

            <Group gap="sm" mt={4} wrap="wrap" align="center">
              <FavoriteHeart objectId={obj.id} favorited={obj.favorited} size={20} />
              {obj.listing?.url && (
                <Anchor
                  href={obj.listing.url}
                  target="_blank"
                  rel="noreferrer"
                  size="xs"
                  c="indigo.6"
                  onClick={(e) => e.stopPropagation()}
                >
                  🌐 Inserat
                </Anchor>
              )}
              <Anchor
                href={buildMapsUrl(obj)}
                target="_blank"
                rel="noreferrer"
                size="xs"
                c="indigo.6"
                onClick={(e) => e.stopPropagation()}
              >
                🗺 Maps
              </Anchor>
              <Anchor
                component={Link}
                to={`/objects/${obj.id}`}
                size="xs"
                c="indigo.6"
                onClick={(e) => e.stopPropagation()}
              >
                📊 Details
              </Anchor>
            </Group>
          </Stack>
        </Group>
      </Card>
    </div>
  );
}

function FactInline({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <Stack gap={0}>
      <Text
        size="xs"
        c="dimmed"
        tt="uppercase"
        fw={500}
        style={{ letterSpacing: '0.04em', fontSize: '0.65rem' }}
      >
        {label}
      </Text>
      <Text fw={600} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
    </Stack>
  );
}
